import { useMemo } from "react";
import { View, ScrollView } from "react-native";
import {
  Typography,
  DrawerContent,
  DrawerActions,
  Button,
  VStack,
  ActivityIndicator,
} from "@smartrent/ui";
import * as Yup from "yup";

import { useCMWFeatureFlagDetails } from "../hooks/useCMWFeatureFlagDetails";
import { CMWFeatureFlagState } from "../types";

import { CMWFlagListItem } from "./CMWFlagListItem";
import { CMWFeatureFlagForm } from "./CMWFeatureFlagForm";
import { sharedStyles } from "./styles";

export function CMWOrgFlagsDetails({
  organizationId,
  organizationName,
}: {
  organizationId: number;
  organizationName: string;
}) {
  const {
    isAddingFlag,
    hierarchyData,
    hierarchyLoading,
    flagOptions,
    handleAddFlag,
    handleCancelAdd,
    handleToggleFlag,
    handleRemoveFlag,
    handleSaveFlag,
  } = useCMWFeatureFlagDetails("organization");

  const orgFlags = useMemo(() => {
    if (!hierarchyData) {
      return [];
    }

    const orgIdStr = organizationId.toString();
    const orgData = hierarchyData.organizations[orgIdStr];
    const allData = hierarchyData.organizations["all"];

    const orgFlags = orgData?.organization_level_flags || [];
    const allFlags = allData?.organization_level_flags || [];

    const flagMap = new Map<string, CMWFeatureFlagState>();

    orgFlags.forEach((flag) => {
      flagMap.set(flag.name, { enabled: flag.enabled, name: flag.name });
    });

    allFlags.forEach((flag) => {
      if (!flagMap.has(flag.name)) {
        flagMap.set(flag.name, { enabled: flag.enabled, name: flag.name });
      }
    });

    return Array.from(flagMap.values()).sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  }, [hierarchyData, organizationId]);

  const handleSaveNewFlag = async (values: {
    featureFlag: string;
    enabled: boolean;
  }) => {
    await handleSaveFlag(values, organizationId);
  };

  if (hierarchyLoading) {
    return (
      <View style={sharedStyles.loadingContainer}>
        <ActivityIndicator size={24} />
      </View>
    );
  }

  const formContent = isAddingFlag ? (
    <CMWFeatureFlagForm
      title={`Org Flags for ${organizationName}`}
      flagOptions={flagOptions}
      initialValues={{
        featureFlag: "",
        enabled: false,
      }}
      validationSchema={Yup.object({
        featureFlag: Yup.string().required("You must select a feature flag"),
      })}
      onSubmit={handleSaveNewFlag}
      onCancel={handleCancelAdd}
      enabledAssistiveText="Whether the feature is enabled or not for this organization"
    />
  ) : (
    <>
      <DrawerContent>
        <ScrollView>
          <View style={sharedStyles.container}>
            <Typography type="title3">
              Org Flags for {organizationName}
            </Typography>
            <VStack spacing={16} style={sharedStyles.content}>
              {orgFlags.length === 0 ? (
                <Typography color="textDisabled">
                  No organization flags
                </Typography>
              ) : (
                <View style={sharedStyles.flagsList}>
                  {orgFlags.map((flag, idx) => (
                    <CMWFlagListItem
                      key={flag.name}
                      flagName={flag.name}
                      enabled={flag.enabled}
                      onToggle={() =>
                        handleToggleFlag(
                          flag.name,
                          flag.enabled,
                          organizationId,
                          organizationId
                        )
                      }
                      onRemove={() =>
                        handleRemoveFlag(flag.name, organizationId)
                      }
                      showSeparator={idx < orgFlags.length - 1}
                    />
                  ))}
                </View>
              )}
            </VStack>
          </View>
        </ScrollView>
      </DrawerContent>
      <DrawerActions>
        <Button onPress={handleAddFlag} style={sharedStyles.fullWidthButton}>
          Add Organization Flag
        </Button>
      </DrawerActions>
    </>
  );

  return formContent;
}
